import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  PageHeader,
  Button,
  TextField,
  Select,
  Switch,
  Segmented,
  useSnackbar,
  Skeleton,
  DownloadIcon,
  type SelectOption,
} from '@/ui'
import type { Settings, PaymentMethod, PaymentFeeRates } from '@garage/shared'
import { api, ApiError } from '@/lib/api.js'
import { ActivityInfo } from '@/components/ActivityInfo.js'
import { SoftwareUpdatePanel } from '@/components/settings/SoftwareUpdatePanel.js'
import { StaffAccountsSection } from '@/components/settings/StaffAccountsSection.js'
import { PasskeysSection } from '@/components/settings/PasskeysSection.js'
import { ActivityFeedSection } from '@/components/settings/ActivityFeedSection.js'
import { ExcelImportField } from '@/components/settings/ExcelImportField.js'
import { qk } from '@/lib/query-keys.js'
import { useSettings, useUnsavedChangesWarning } from '@/lib/hooks.js'
import { parseMoneyToMinor, formatMinor, PAYMENT_METHODS } from '@garage/shared'

type CategoryId = 'business' | 'moneyAndDocuments' | 'workshop' | 'customerFacing' | 'accessAndSystem'

const CATEGORIES: Array<{ id: CategoryId; labelKey: string }> = [
  { id: 'business', labelKey: 'settings.groups.business' },
  { id: 'moneyAndDocuments', labelKey: 'settings.groups.moneyAndDocuments' },
  { id: 'workshop', labelKey: 'settings.groups.workshop' },
  { id: 'customerFacing', labelKey: 'settings.groups.customerFacing' },
  { id: 'accessAndSystem', labelKey: 'settings.groups.accessAndSystem' },
]

/**
 * Field-to-category map for error tracking.
 * Used to determine which category a field belongs to when showing error dots.
 */
const FIELD_TO_CATEGORY: Record<string, CategoryId> = {
  businessName: 'business',
  businessEmail: 'business',
  businessPhone: 'business',
  website: 'business',
  taxNumber: 'business',
  businessAddress: 'business',
  logoPath: 'business',
  bankName: 'business',
  bankAccountTitle: 'business',
  bankAccountNumber: 'business',
  bankIban: 'business',
  bankBranchCode: 'business',
  currencyCode: 'moneyAndDocuments',
  currencySymbol: 'moneyAndDocuments',
  currencyDecimals: 'moneyAndDocuments',
  vatEnabledByDefault: 'moneyAndDocuments',
  vatRateBp: 'moneyAndDocuments',
  vatLabel: 'moneyAndDocuments',
  jobcardPrefix: 'moneyAndDocuments',
  quotationPrefix: 'moneyAndDocuments',
  invoicePrefix: 'moneyAndDocuments',
  numberPadding: 'moneyAndDocuments',
  numberIncludesYear: 'moneyAndDocuments',
  quotationValidityDays: 'moneyAndDocuments',
  invoicePaymentTermsDays: 'moneyAndDocuments',
  documentFooterTerms: 'moneyAndDocuments',
  bankDetails: 'moneyAndDocuments',
  dateFormat: 'moneyAndDocuments',
  timezone: 'moneyAndDocuments',
  defaultCountryCode: 'moneyAndDocuments',
  defaultLabourRateMinor: 'workshop',
  whatsappEnabled: 'workshop',
  showMargin: 'workshop',
  appointmentsEnabled: 'workshop',
  accountingMode: 'workshop',
  latePolicyEnabled: 'workshop',
  latePolicyOpenTime: 'workshop',
  latePolicyBufferMinutes: 'workshop',
  latePolicyMultiplier: 'workshop',
  latePolicyWorkingDaysPerMonth: 'workshop',
  latePolicyShiftMinutes: 'workshop',
  overtimePolicyEnabled: 'workshop',
  overtimePolicyCloseTime: 'workshop',
  overtimePolicyBufferMinutes: 'workshop',
  overtimePolicyMultiplier: 'workshop',
  whatsappTemplate: 'customerFacing',
  publicBaseUrl: 'customerFacing',
}

function getFieldCategory(fieldKey: string): CategoryId | undefined {
  if (fieldKey in FIELD_TO_CATEGORY) {
    return FIELD_TO_CATEGORY[fieldKey as keyof typeof FIELD_TO_CATEGORY]
  }
  if (fieldKey.startsWith('paymentFeeRates.')) {
    return 'moneyAndDocuments'
  }
  if (fieldKey.startsWith('excelSync')) {
    return 'moneyAndDocuments'
  }
  return undefined
}

export function SettingsPage() {
  const { data: settings, isLoading } = useSettings()
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const logoInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  // Active category from URL, defaulting to 'business'
  const activeSection = (searchParams.get('section') || 'business') as CategoryId
  const isValidSection = CATEGORIES.some(c => c.id === activeSection)
  const validActiveSection: CategoryId = isValidSection ? activeSection : 'business'

  const selectSection = (sectionId: CategoryId) => {
    setSearchParams(prev => {
      const n = new URLSearchParams(prev)
      n.set('section', sectionId)
      return n
    }, { replace: true })
  }

  // Form state
  const [formData, setFormData] = useState<Partial<Settings>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string>('')
  /** Bumped after an upload so the fixed logo URL is refetched, not cached. */
  const [logoCacheBust, setLogoCacheBust] = useState(0)

  // Money input display values
  const [labourRateDisplay, setLabourRateDisplay] = useState('')
  const [vatRatePercentDisplay, setVatRatePercentDisplay] = useState('')
  // Payment fee rates display (rateBp as percent, fixedMinor as money string)
  const [feeRatesDisplay, setFeeRatesDisplay] = useState<Record<PaymentMethod, { ratePct: string; fixedStr: string }>>({
    cash: { ratePct: '', fixedStr: '' },
    bank_transfer: { ratePct: '', fixedStr: '' },
    card_machine: { ratePct: '', fixedStr: '' },
    link_tabby: { ratePct: '', fixedStr: '' },
    link_tamara: { ratePct: '', fixedStr: '' },
    link_card: { ratePct: '', fixedStr: '' },
    other: { ratePct: '', fixedStr: '' },
  })

  const buildFeeRatesDisplay = (s: Settings | undefined): Record<PaymentMethod, { ratePct: string; fixedStr: string }> => {
    if (!s) {
      return {
        cash: { ratePct: '', fixedStr: '' },
        bank_transfer: { ratePct: '', fixedStr: '' },
        card_machine: { ratePct: '', fixedStr: '' },
        link_tabby: { ratePct: '', fixedStr: '' },
        link_tamara: { ratePct: '', fixedStr: '' },
        link_card: { ratePct: '', fixedStr: '' },
        other: { ratePct: '', fixedStr: '' },
      }
    }
    const display: Record<PaymentMethod, { ratePct: string; fixedStr: string }> = {} as any
    for (const method of PAYMENT_METHODS) {
      const rate = s.paymentFeeRates?.[method]
      display[method] = {
        ratePct: rate && rate.rateBp > 0 ? (rate.rateBp / 100).toFixed(2).replace(/\.?0+$/, '') : '',
        fixedStr: rate && rate.fixedMinor > 0 ? formatMinor(rate.fixedMinor, { decimals: 2 }) : '',
      }
    }
    return display
  }

  // Initialize form from settings
  useEffect(() => {
    if (settings) {
      setFormData(settings)
      setLabourRateDisplay(settings.defaultLabourRateMinor ? formatMinor(settings.defaultLabourRateMinor, { decimals: 2 }) : '')
      setVatRatePercentDisplay(String((settings.vatRateBp ?? 500) / 100))
      setFeeRatesDisplay(buildFeeRatesDisplay(settings))
      // Only set logo preview if a logo path exists; otherwise leave blank to show placeholder.
      if (settings.logoPath) {
        setLogoPreview(logoCacheBust ? `${api.settings.logoUrl()}?v=${logoCacheBust}` : api.settings.logoUrl())
      } else {
        setLogoPreview('')
      }
    }
  }, [settings, logoCacheBust])

  // Track dirty state
  const isDirty = useMemo(() => {
    if (!settings) return false
    const originalFeeDisplay = buildFeeRatesDisplay(settings)
    return (
      JSON.stringify(formData) !== JSON.stringify(settings) ||
      logoFile !== null ||
      labourRateDisplay !== (settings.defaultLabourRateMinor ? formatMinor(settings.defaultLabourRateMinor, { decimals: 2 }) : '') ||
      vatRatePercentDisplay !== String((settings.vatRateBp ?? 500) / 100) ||
      JSON.stringify(feeRatesDisplay) !== JSON.stringify(originalFeeDisplay)
    )
  }, [formData, logoFile, labourRateDisplay, vatRatePercentDisplay, feeRatesDisplay, settings])

  useUnsavedChangesWarning(isDirty)

  // Update form field
  const updateField = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }))
    setErrors(prev => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  // Mutation for saving
  const { mutate: saveMutation, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      // Prepare update data.
      //
      // logoPath is deliberately dropped. It is owned by the upload route and
      // is NOT an editable form field: sending the copy that formData picked
      // up when the page loaded would overwrite whatever the upload below just
      // set, which is exactly how a saved logo used to vanish on the next
      // visit while its file sat untouched in uploads/.
      const { logoPath: _serverOwned, ...updateData } = formData as Partial<Settings>

      // Convert labour rate
      if (labourRateDisplay !== (settings?.defaultLabourRateMinor ? formatMinor(settings.defaultLabourRateMinor, { decimals: 2 }) : '')) {
        const parsed = labourRateDisplay.trim() ? parseMoneyToMinor(labourRateDisplay) : 0
        if (parsed === null) {
          setErrors(prev => ({ ...prev, defaultLabourRateMinor: t('settings.errors.invalidAmount') }))
          throw new Error('Invalid labour rate')
        }
        updateData.defaultLabourRateMinor = parsed
      }

      // Convert VAT rate from percent to basis points
      if (vatRatePercentDisplay !== String((settings?.vatRateBp ?? 500) / 100)) {
        const percent = Number(vatRatePercentDisplay)
        if (isNaN(percent) || percent < 0 || percent > 100) {
          setErrors(prev => ({ ...prev, vatRateBp: t('settings.errors.rateMustBe0to100') }))
          throw new Error('Invalid VAT rate')
        }
        updateData.vatRateBp = Math.round(percent * 100)
      }

      // Convert payment fee rates from display to storage format
      const originalFeeDisplay = buildFeeRatesDisplay(settings)
      if (JSON.stringify(feeRatesDisplay) !== JSON.stringify(originalFeeDisplay)) {
        const rates: PaymentFeeRates = {} as any
        for (const method of PAYMENT_METHODS) {
          const display = feeRatesDisplay[method]
          const ratePctStr = display?.ratePct.trim() || ''
          const fixedStr = display?.fixedStr.trim() || ''

          let rateBp = 0
          if (ratePctStr) {
            if (!/^\d+(\.\d{1,2})?$/.test(ratePctStr)) {
              setErrors(prev => ({ ...prev, [`paymentFeeRates.${method}.rateBp`]: t('settings.errors.invalidPercent') }))
              throw new Error(`Invalid rate for ${method}`)
            }
            rateBp = Math.round(parseFloat(ratePctStr) * 100)
            if (rateBp > 10000) {
              setErrors(prev => ({ ...prev, [`paymentFeeRates.${method}.rateBp`]: t('settings.errors.rateTooHigh') }))
              throw new Error(`Rate too high for ${method}`)
            }
          }

          let fixedMinor = 0
          if (fixedStr) {
            const parsed = parseMoneyToMinor(fixedStr)
            if (parsed === null) {
              setErrors(prev => ({ ...prev, [`paymentFeeRates.${method}.fixedMinor`]: t('settings.errors.invalidAmount') }))
              throw new Error(`Invalid fixed fee for ${method}`)
            }
            fixedMinor = parsed
          }

          rates[method] = { rateBp, fixedMinor }
        }
        updateData.paymentFeeRates = rates
      }

      // Upload logo if changed
      if (logoFile) {
        await api.settings.uploadLogo(logoFile)
        setLogoFile(null)
        // The logo URL never changes, so the browser would keep serving the
        // previous image from cache.
        setLogoCacheBust(Date.now())
      }

      // Save settings
      await api.settings.update(updateData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.settings })
      queryClient.invalidateQueries({ queryKey: qk.dashboard })
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      snackbar.success(t('settings.settingsSaved'))
      setErrors({})
    },
    onError: (err: Error) => {
      if (err instanceof ApiError) {
        setErrors(err.fieldErrors)
      }
      if (!err.message.includes('Invalid')) {
        snackbar.error(t('settings.failedToSave'))
      }
    },
  })

  const handleSubmit = () => {
    lastErrorKeyRef.current = ''
    setErrors({})
    saveMutation()
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0]
    if (!file) return

    // Validate file type
    // Raster only: an uploaded SVG is script on this origin, and pdfmake embeds
    // the logo as a raster data URI, so an SVG would never reach a document.
    const validTypes = ['image/png', 'image/jpeg', 'image/webp']
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, logoPath: t('settings.errors.mustBeImage') }))
      return
    }

    setLogoFile(file)
    const reader = new FileReader()
    reader.onload = (evt: ProgressEvent<FileReader>) => {
      setLogoPreview(evt.target?.result as string)
    }
    reader.readAsDataURL(file)
    setErrors(prev => {
      const next = { ...prev }
      delete next.logoPath
      return next
    })
  }

  // Generate numbering example
  const numberExample = useMemo(() => {
    if (!settings) return ''
    const year = settings.numberIncludesYear ? '-2026' : ''
    const number = String(1).padStart(settings.numberPadding, '0')
    return `${formData.jobcardPrefix || settings.jobcardPrefix}-${number}${year}`
  }, [formData, settings])

  // Track which categories have errors
  const categoriesWithErrors = useMemo(() => {
    return CATEGORIES.filter(cat => {
      return Object.entries(errors).some(([fieldKey]) => {
        return getFieldCategory(fieldKey) === cat.id
      })
    }).map(c => c.id)
  }, [errors])

  // Auto-switch to first category with errors when they appear
  const errorCategoryKey = useMemo(() => categoriesWithErrors.join(','), [categoriesWithErrors])
  const lastErrorKeyRef = useRef('')
  useEffect(() => {
    if (errorCategoryKey === lastErrorKeyRef.current) return
    lastErrorKeyRef.current = errorCategoryKey
    const firstErrorCategory = categoriesWithErrors[0]
    if (firstErrorCategory && !categoriesWithErrors.includes(validActiveSection)) {
      selectSection(firstErrorCategory)
    }
  }, [errorCategoryKey, categoriesWithErrors, validActiveSection, selectSection])

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Skeleton lines={10} />
      </div>
    )
  }

  if (!settings || !formData.businessName) {
    return <div className="text-on-surface-variant text-center py-8">{t('settings.failedToLoad')}</div>
  }

  const dateFormatOptions: SelectOption[] = [
    { value: 'dd/MM/yyyy', label: 'DD/MM/YYYY' },
    { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD' },
    { value: 'MM/dd/yyyy', label: 'MM/DD/YYYY' },
  ]

  return (
    <div>
      <PageHeader
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
        actions={
          <Button
            variant="filled"
            disabled={!isDirty || isSaving}
            loading={isSaving}
            onClick={handleSubmit}
          >
            {t('common.save')}
          </Button>
        }
      />

      <ActivityInfo entityType="settings" entityId="singleton" />

      {/*
        Master-detail layout: left nav shows category list, right pane shows
        cards for the selected category. Form categories are always mounted to
        preserve state, but hidden when inactive; accessAndSystem panels are
        conditional-rendered as they have no form state.

        The old masonry layout reference: Chromium has a long-standing bug
        where `columns` inside a scrolling ancestor inflates scroll height; a
        grid avoids it.
      */}
      <div className="mt-6 lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-6 lg:items-start">
        {/* Left navigation pane */}
        <nav
          aria-label={t('settings.categoriesLabel')}
          className="lg:sticky lg:top-4"
        >
          <div className="hidden lg:flex lg:flex-col lg:gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                type="button"
                onClick={() => selectSection(cat.id)}
                aria-current={validActiveSection === cat.id ? 'page' : undefined}
                className={`px-4 py-2 rounded-lg text-label-md text-start transition-colors interactive focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-outline ${
                  validActiveSection === cat.id
                    ? 'bg-surface-high text-on-surface border-s-4 border-primary'
                    : 'bg-surface-container text-on-surface border-s-4 border-transparent hover:bg-surface-high'
                } ${categoriesWithErrors.includes(cat.id) ? 'relative' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <span className="flex-1">{t(cat.labelKey)}</span>
                  {categoriesWithErrors.includes(cat.id) && (
                    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-error" aria-label={t('common.errorOccurred')} />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Mobile horizontal pill buttons */}
          <div className="flex lg:hidden overflow-x-auto gap-2 pb-2 -mx-4 px-4">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                type="button"
                onClick={() => selectSection(cat.id)}
                aria-current={validActiveSection === cat.id ? 'page' : undefined}
                className={`px-3 py-1.5 rounded-full text-label-sm shrink-0 whitespace-nowrap transition-colors interactive focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-outline ${
                  validActiveSection === cat.id
                    ? 'bg-primary text-on-primary'
                    : 'bg-surface-container text-on-surface border border-outline-variant'
                }`}
              >
                {t(cat.labelKey)}
              </button>
            ))}
          </div>
        </nav>

        {/* Right content pane */}
        <div className="flex flex-col gap-4">
          {/* Business section */}
          <div className={validActiveSection === 'business' ? 'flex flex-col gap-4' : 'hidden'}>
      {/* Business Identity */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.businessIdentity.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.businessIdentity.subtitle')}</p>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-4 mt-4">
            <TextField
              label={t('settings.businessIdentity.businessName')}
              value={formData.businessName || ''}
              onChange={(v) => updateField('businessName', v)}
              error={errors.businessName}
              fullWidth
            />

            <TextField
              label={t('settings.businessIdentity.email')}
              value={formData.businessEmail || ''}
              onChange={(v) => updateField('businessEmail', v)}
              error={errors.businessEmail}
              fullWidth
            />

            <TextField
              label={t('settings.businessIdentity.phone')}
              value={formData.businessPhone || ''}
              onChange={(v) => updateField('businessPhone', v)}
              error={errors.businessPhone}
              fullWidth
            />

            <TextField
              label={t('settings.businessIdentity.website')}
              value={formData.website || ''}
              onChange={(v) => updateField('website', v)}
              error={errors.website}
              fullWidth
            />

            <TextField
              label={t('settings.businessIdentity.taxNumber')}
              value={formData.taxNumber || ''}
              onChange={(v) => updateField('taxNumber', v)}
              error={errors.taxNumber}
              fullWidth
            />

            <TextField
              label={t('settings.businessIdentity.address')}
              value={formData.businessAddress || ''}
              onChange={(v) => updateField('businessAddress', v)}
              multiline
              rows={3}
              error={errors.businessAddress}
              fullWidth
              className="xl:col-span-2"
            />
          </div>

          {/* Logo */}
          <div className="space-y-2 mt-4">
            <label className="block text-label-md text-on-surface">{t('settings.businessIdentity.logo')}</label>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-shrink-0 w-32 h-32 bg-surface-high rounded-lg flex items-center justify-center overflow-hidden border border-outline-variant">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt={t('settings.businessIdentity.logoPreviewAlt')}
                    className="max-w-full max-h-full"
                    onError={() => setLogoPreview('')}
                  />
                ) : (
                  <p className="text-label-sm text-on-surface-subtle text-center px-2">{t('settings.businessIdentity.noLogo')}</p>
                )}
              </div>
              <div className="flex-1">
                {/* display:none + ref.click(), not sr-only: see ExcelImportField. */}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleLogoChange}
                  className="hidden"
                />
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    className="inline-flex items-center justify-center px-4 py-2 rounded-full border border-outline bg-transparent text-on-surface text-label-lg interactive hover:bg-surface-high active:bg-surface-high cursor-pointer w-fit"
                  >
                    {t('settings.businessIdentity.uploadLogo')}
                  </button>
                  {logoFile && (
                    <p className="text-body-sm text-on-surface-variant">{logoFile.name}</p>
                  )}
                </div>
                <p className="text-body-sm text-on-surface-variant mt-2">{t('settings.businessIdentity.logoHelp')}</p>
                {errors.logoPath && <p className="text-body-sm text-error">{errors.logoPath}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bank details */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.bankDetails')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.bankSection.subtitle')}</p>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-4 mt-4">
            <TextField
              label={t('settings.bankSection.bankName')}
              value={formData.bankName || ''}
              onChange={(v) => updateField('bankName', v)}
              error={errors.bankName}
              fullWidth
            />

            <TextField
              label={t('settings.bankSection.accountTitle')}
              value={formData.bankAccountTitle || ''}
              onChange={(v) => updateField('bankAccountTitle', v)}
              error={errors.bankAccountTitle}
              fullWidth
            />

            <TextField
              label={t('settings.bankSection.accountNumber')}
              value={formData.bankAccountNumber || ''}
              onChange={(v) => updateField('bankAccountNumber', v)}
              error={errors.bankAccountNumber}
              fullWidth
            />

            <TextField
              label={t('settings.bankSection.iban')}
              value={formData.bankIban || ''}
              onChange={(v) => updateField('bankIban', v)}
              error={errors.bankIban}
              fullWidth
            />

            <TextField
              label={t('settings.bankSection.branchCode')}
              value={formData.bankBranchCode || ''}
              onChange={(v) => updateField('bankBranchCode', v)}
              error={errors.bankBranchCode}
              fullWidth
            />
          </div>
        </div>
      </div>
          </div>

          {/* Money & Documents section */}
          <div className={validActiveSection === 'moneyAndDocuments' ? 'flex flex-col gap-4' : 'hidden'}>
          {/* Money */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.money.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.money.subtitle')}</p>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-4 mt-4">
            <TextField
              label={t('settings.money.currencyCode')}
              value={formData.currencyCode || ''}
              onChange={(v) => updateField('currencyCode', v)}
              error={errors.currencyCode}
              maxLength={8}
              fullWidth
            />

            <TextField
              label={t('settings.money.currencySymbol')}
              value={formData.currencySymbol || ''}
              onChange={(v) => updateField('currencySymbol', v)}
              error={errors.currencySymbol}
              maxLength={8}
              fullWidth
            />

            <Select
              label={t('settings.money.decimalPlaces')}
              value={String(formData.currencyDecimals || 2)}
              onChange={(v: string) => updateField('currencyDecimals', Number(v) as 0 | 1 | 2)}
              options={[
                { value: '0', label: t('settings.money.decimals0') },
                { value: '1', label: t('settings.money.decimals1') },
                { value: '2', label: t('settings.money.decimals2') },
              ]}
              error={errors.currencyDecimals}
              fullWidth
            />
          </div>
        </div>
      </div>

      {/* VAT */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.vat.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.vat.subtitle')}</p>

          <div className="space-y-4 mt-4">
            <Switch
              checked={formData.vatEnabledByDefault || false}
              onChange={(v) => updateField('vatEnabledByDefault', v)}
              label={t('settings.vat.onByDefault')}
            />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-4">
              <TextField
                label={t('settings.vat.ratePercent')}
                value={vatRatePercentDisplay}
                onChange={setVatRatePercentDisplay}
                numeric
                error={errors.vatRateBp}
                fullWidth
              />

              <TextField
                label={t('settings.vat.label')}
                value={formData.vatLabel || ''}
                onChange={(v) => updateField('vatLabel', v)}
                supportingText={t('settings.vat.labelHelp')}
                error={errors.vatLabel}
                maxLength={20}
                fullWidth
              />
            </div>
          </div>
        </div>
      </div>

      {/* Payment processing fees */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.paymentProcessingFees.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.paymentProcessingFees.subtitle')}</p>

          <div className="space-y-4 mt-4">
            <p className="text-body-sm text-on-surface-variant">{t('settings.paymentProcessingFees.help')}</p>

            <div className="space-y-3">
              {PAYMENT_METHODS.map((method) => (
                <div key={method} className="grid grid-cols-1 xl:grid-cols-3 gap-x-5 gap-y-2 p-3 bg-surface-high rounded-md border border-outline-variant">
                  <div className="text-label-md text-on-surface pt-1">
                    {t(`paymentMethods.${method}`)}
                  </div>
                  <TextField
                    label={t('settings.paymentProcessingFees.percentageLabel')}
                    value={feeRatesDisplay[method]?.ratePct || ''}
                    onChange={(v) => setFeeRatesDisplay(prev => ({
                      ...prev,
                      [method]: { ...prev[method], ratePct: v }
                    }))}
                    placeholder="0.00"
                    numeric
                    error={errors[`paymentFeeRates.${method}.rateBp`]}
                    fullWidth
                  />
                  <TextField
                    label={t('settings.paymentProcessingFees.fixedFeeLabel')}
                    value={feeRatesDisplay[method]?.fixedStr || ''}
                    onChange={(v) => setFeeRatesDisplay(prev => ({
                      ...prev,
                      [method]: { ...prev[method], fixedStr: v }
                    }))}
                    placeholder="0.00"
                    numeric
                    error={errors[`paymentFeeRates.${method}.fixedMinor`]}
                    fullWidth
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Numbering */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.numbering.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.numbering.subtitle')}</p>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-4">
              <TextField
                label={t('settings.numbering.jobcardPrefix')}
                value={formData.jobcardPrefix || ''}
                onChange={(v) => updateField('jobcardPrefix', v)}
                error={errors.jobcardPrefix}
                maxLength={10}
                fullWidth
              />

              <TextField
                label={t('settings.numbering.quotationPrefix')}
                value={formData.quotationPrefix || ''}
                onChange={(v) => updateField('quotationPrefix', v)}
                error={errors.quotationPrefix}
                maxLength={10}
                fullWidth
              />

              <TextField
                label={t('settings.numbering.invoicePrefix')}
                value={formData.invoicePrefix || ''}
                onChange={(v) => updateField('invoicePrefix', v)}
                error={errors.invoicePrefix}
                maxLength={10}
                fullWidth
              />

              <TextField
                label={t('settings.numbering.numberPadding')}
                value={String(formData.numberPadding || 4)}
                onChange={(v: string) => {
                  const n = Number(v)
                  if (!isNaN(n) && n >= 1 && n <= 10) {
                    updateField('numberPadding', n)
                  }
                }}
                numeric
                error={errors.numberPadding}
                fullWidth
              />
            </div>

            <Switch
              checked={formData.numberIncludesYear || false}
              onChange={(v) => updateField('numberIncludesYear', v)}
              label={t('settings.numbering.includeYear')}
            />

            <div className="p-4 bg-surface-high rounded-md text-on-surface text-body-sm border border-outline-variant">
              {t('settings.numbering.example')}: <span className="ref ms-2">{numberExample}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.documents.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.documents.subtitle')}</p>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-4 mt-4">
            <TextField
              label={t('settings.documents.quotationValidityDays')}
              value={String(formData.quotationValidityDays || 14)}
              onChange={(v: string) => {
                const n = Number(v)
                if (!isNaN(n) && n >= 0 && n <= 365) {
                  updateField('quotationValidityDays', n)
                }
              }}
              numeric
              error={errors.quotationValidityDays}
              fullWidth
            />

            <TextField
              label={t('settings.documents.invoicePaymentTermsDays')}
              value={String(formData.invoicePaymentTermsDays || 0)}
              onChange={(v: string) => {
                const n = Number(v)
                if (!isNaN(n) && n >= 0 && n <= 365) {
                  updateField('invoicePaymentTermsDays', n)
                }
              }}
              numeric
              supportingText={t('settings.documents.dueOnReceipt')}
              error={errors.invoicePaymentTermsDays}
              fullWidth
            />

            <TextField
              label={t('settings.documents.footerTerms')}
              value={formData.documentFooterTerms || ''}
              onChange={(v) => updateField('documentFooterTerms', v)}
              multiline
              rows={4}
              supportingText={t('settings.documents.footerTermsHelp')}
              error={errors.documentFooterTerms}
              fullWidth
              className="xl:col-span-2"
            />

            <TextField
              label={t('settings.bankDetails')}
              value={formData.bankDetails || ''}
              onChange={(v) => updateField('bankDetails', v)}
              multiline
              rows={3}
              supportingText={t('settings.documents.bankDetailsHelp')}
              error={errors.bankDetails}
              fullWidth
              className="xl:col-span-2"
            />
          </div>
        </div>
      </div>

      {/* Localisation */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.localisation.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.localisation.subtitle')}</p>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-4 mt-4">
            <Select
              label={t('settings.localisation.dateFormat')}
              value={formData.dateFormat || 'dd/MM/yyyy'}
              onChange={(v: string) => updateField('dateFormat', v as 'dd/MM/yyyy' | 'yyyy-MM-dd' | 'MM/dd/yyyy')}
              options={dateFormatOptions}
              error={errors.dateFormat}
              fullWidth
            />

            <TextField
              label={t('settings.localisation.timezone')}
              value={formData.timezone || ''}
              onChange={(v) => updateField('timezone', v)}
              supportingText={t('settings.localisation.timezoneHelp')}
              error={errors.timezone}
              fullWidth
            />

            <TextField
              label={t('settings.localisation.defaultCountryCode')}
              value={formData.defaultCountryCode ?? ''}
              onChange={(v) => updateField('defaultCountryCode', v)}
              supportingText={t('settings.localisation.defaultCountryCodeHelp')}
              error={errors.defaultCountryCode}
              placeholder="+971"
              fullWidth
            />
          </div>
        </div>
      </div>

      {/* Excel sync */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.excelSync.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">
            {t('settings.excelSync.subtitle')}
          </p>

          <div className="mt-4 p-4 bg-surface-high rounded-md text-body-sm text-on-surface-variant border border-outline-variant">
            <p>
              {t('settings.excelSync.columnsIntro')}: Date, Invoice Number, Supplier Name,
              Supplier TRN, Amount Before VAT, VAT. {t('settings.excelSync.uploadReplaces')}
            </p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-6 mt-4">
            <ExcelImportField category="general" label={t('accounting.categories.general')} />
            <ExcelImportField category="parts" label={t('accounting.categories.parts')} />
            <ExcelImportField category="paint" label={t('accounting.categories.paint')} />
          </div>
        </div>
      </div>
          </div>

          {/* Workshop section */}
          <div className={validActiveSection === 'workshop' ? 'flex flex-col gap-4' : 'hidden'}>
          {/* Defaults */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.defaults.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.defaults.subtitle')}</p>

          <div className="mt-4">
            <TextField
              label={t('settings.defaults.defaultLabourRate')}
              value={labourRateDisplay}
              onChange={setLabourRateDisplay}
              numeric
              error={errors.defaultLabourRateMinor}
              fullWidth
            />
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.features.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.features.subtitle')}</p>

          <div className="space-y-3 mt-4">
            <div>
              <Switch
                checked={formData.whatsappEnabled || false}
                onChange={(v) => updateField('whatsappEnabled', v)}
                label={t('settings.features.whatsappButton')}
              />
              <p className="text-body-sm text-on-surface-variant ms-6 mt-1">{t('settings.features.whatsappButtonHelp')}</p>
            </div>

            <div>
              <Switch
                checked={formData.showMargin || false}
                onChange={(v) => updateField('showMargin', v)}
                label={t('settings.features.showMargin')}
              />
              <p className="text-body-sm text-on-surface-variant ms-6 mt-1">{t('settings.features.showMarginHelp')}</p>
            </div>

            <div>
              <Switch
                checked={formData.appointmentsEnabled || false}
                onChange={(v) => updateField('appointmentsEnabled', v)}
                label={t('settings.features.appointments')}
              />
              <p className="text-body-sm text-on-surface-variant ms-6 mt-1">{t('settings.features.appointmentsHelp')}</p>
            </div>

            <div>
              <Segmented
                label={t('settings.features.accountingMode')}
                value={formData.accountingMode || 'simplified'}
                onChange={(v: string) => updateField('accountingMode', v as 'simplified' | 'advanced')}
                options={[
                  { value: 'simplified', label: t('settings.features.accountingModeSimplified') },
                  { value: 'advanced', label: t('settings.features.accountingModeAdvanced') },
                ]}
              />
              <p className="text-body-sm text-on-surface-variant ms-0 mt-2">{t('settings.features.accountingModeHelp')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Late-arrival policy */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.latePolicy.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.latePolicy.subtitle')}</p>

          <div className="space-y-4 mt-4">
            <Switch
              checked={formData.latePolicyEnabled || false}
              onChange={(v) => updateField('latePolicyEnabled', v)}
              label={t('settings.latePolicy.enabled')}
            />

            {/*
              Always rendered, just disabled when the policy is off — matching
              how VAT rate/label stay visible regardless of "VAT on by
              default" elsewhere on this page. This page's two-column layout
              already has one documented Chromium repaint bug (see the
              masonry-layout comment above) from a scrolling ancestor whose
              content height changes; mounting/unmounting a whole block of
              fields on toggle reproduced the same class of bug (the screen
              would go black below the fold until something forced a
              repaint). Keeping the section's height constant avoids it.
            */}
            <div className={`grid grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-4 ${formData.latePolicyEnabled ? '' : 'opacity-60'}`}>
              <TextField
                label={t('settings.latePolicy.openTime')}
                type="time"
                value={formData.latePolicyOpenTime || '08:30'}
                onChange={(v) => updateField('latePolicyOpenTime', v)}
                error={errors.latePolicyOpenTime}
                disabled={!formData.latePolicyEnabled}
                fullWidth
              />

              <TextField
                label={t('settings.latePolicy.bufferMinutes')}
                value={String(formData.latePolicyBufferMinutes ?? 15)}
                onChange={(v: string) => {
                  const n = Number(v)
                  if (!isNaN(n) && n >= 0 && n <= 240) updateField('latePolicyBufferMinutes', n)
                }}
                integer
                supportingText={t('settings.latePolicy.bufferMinutesHelp')}
                error={errors.latePolicyBufferMinutes}
                disabled={!formData.latePolicyEnabled}
                fullWidth
              />

              <TextField
                label={t('settings.latePolicy.multiplier')}
                value={String(formData.latePolicyMultiplier ?? 2)}
                onChange={(v: string) => {
                  const n = Number(v)
                  if (!isNaN(n) && n >= 1 && n <= 10) updateField('latePolicyMultiplier', n)
                }}
                numeric
                supportingText={t('settings.latePolicy.multiplierHelp')}
                error={errors.latePolicyMultiplier}
                disabled={!formData.latePolicyEnabled}
                fullWidth
              />

              <TextField
                label={t('settings.latePolicy.workingDaysPerMonth')}
                value={String(formData.latePolicyWorkingDaysPerMonth ?? 26)}
                onChange={(v: string) => {
                  const n = Number(v)
                  if (!isNaN(n) && n >= 1 && n <= 31) updateField('latePolicyWorkingDaysPerMonth', n)
                }}
                integer
                error={errors.latePolicyWorkingDaysPerMonth}
                disabled={!formData.latePolicyEnabled}
                fullWidth
              />

              <TextField
                label={t('settings.latePolicy.shiftMinutes')}
                value={String(formData.latePolicyShiftMinutes ?? 480)}
                onChange={(v: string) => {
                  const n = Number(v)
                  if (!isNaN(n) && n >= 1 && n <= 1440) updateField('latePolicyShiftMinutes', n)
                }}
                integer
                supportingText={t('settings.latePolicy.shiftMinutesHelp')}
                error={errors.latePolicyShiftMinutes}
                disabled={!formData.latePolicyEnabled}
                fullWidth
              />
            </div>

            <p className="text-body-sm text-on-surface-variant">{t('settings.latePolicy.explainer')}</p>
          </div>
        </div>
      </div>

      {/* Overtime policy */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.overtimePolicy.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.overtimePolicy.subtitle')}</p>

          <div className="space-y-4 mt-4">
            <Switch
              checked={formData.overtimePolicyEnabled || false}
              onChange={(v) => updateField('overtimePolicyEnabled', v)}
              label={t('settings.overtimePolicy.enabled')}
            />

            {/*
              Always rendered, just disabled when the policy is off — matching
              how VAT rate/label stay visible regardless of "VAT on by
              default" elsewhere on this page. This page's two-column layout
              already has one documented Chromium repaint bug (see the
              masonry-layout comment above) from a scrolling ancestor whose
              content height changes; mounting/unmounting a whole block of
              fields on toggle reproduced the same class of bug (the screen
              would go black below the fold until something forced a
              repaint). Keeping the section's height constant avoids it.
            */}
            <div className={`grid grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-4 ${formData.overtimePolicyEnabled ? '' : 'opacity-60'}`}>
              <TextField
                label={t('settings.overtimePolicy.closeTime')}
                type="time"
                value={formData.overtimePolicyCloseTime || '17:30'}
                onChange={(v) => updateField('overtimePolicyCloseTime', v)}
                error={errors.overtimePolicyCloseTime}
                disabled={!formData.overtimePolicyEnabled}
                fullWidth
              />

              <TextField
                label={t('settings.overtimePolicy.bufferMinutes')}
                value={String(formData.overtimePolicyBufferMinutes ?? 15)}
                onChange={(v: string) => {
                  const n = Number(v)
                  if (!isNaN(n) && n >= 0 && n <= 240) updateField('overtimePolicyBufferMinutes', n)
                }}
                integer
                supportingText={t('settings.overtimePolicy.bufferMinutesHelp')}
                error={errors.overtimePolicyBufferMinutes}
                disabled={!formData.overtimePolicyEnabled}
                fullWidth
              />

              <TextField
                label={t('settings.overtimePolicy.multiplier')}
                value={String(formData.overtimePolicyMultiplier ?? 1.5)}
                onChange={(v: string) => {
                  const n = Number(v)
                  if (!isNaN(n) && n >= 1 && n <= 10) updateField('overtimePolicyMultiplier', n)
                }}
                numeric
                supportingText={t('settings.overtimePolicy.multiplierHelp')}
                error={errors.overtimePolicyMultiplier}
                disabled={!formData.overtimePolicyEnabled}
                fullWidth
              />
            </div>

            <p className="text-body-sm text-on-surface-variant">{t('settings.overtimePolicy.explainer')}</p>
          </div>
        </div>
      </div>

      {/* Inspection checklist */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.inspectionChecklist.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">
            {t('settings.inspectionChecklist.subtitle')}
          </p>

          <p className="text-body-sm text-on-surface mt-4">
            {t('settings.inspectionChecklist.description')}
          </p>

          <Button
            variant="outlined"
            className="mt-4"
            onClick={() => navigate('/settings/inspection-checklist')}
          >
            {t('settings.inspectionChecklist.editChecklist')}
          </Button>
        </div>
      </div>

          </div>

          {/* Customer-facing section */}
          <div className={validActiveSection === 'customerFacing' ? 'flex flex-col gap-4' : 'hidden'}>
          {/* WhatsApp Template */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.whatsapp.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.whatsapp.subtitle')}</p>

          <div className="mt-4">
            <TextField
              label={t('settings.whatsapp.messageTemplate')}
              value={formData.whatsappTemplate || ''}
              onChange={(v) => updateField('whatsappTemplate', v)}
              multiline
              rows={4}
              supportingText={`${t('settings.whatsapp.templateHelpPrefix')} {{client_name}}, {{document_type}}, {{document_number}}, {{car}}, {{total}}, {{business_name}}, {{jobcard_number}}, {{link}}`}
              error={errors.whatsappTemplate}
              fullWidth
            />
          </div>
        </div>
      </div>

      {/* Customer Portal */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.customerPortal.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.customerPortal.subtitle')}</p>

          <div className="mt-4">
            <TextField
              label={t('settings.customerPortal.publicBaseUrl')}
              value={formData.publicBaseUrl || ''}
              onChange={(v) => updateField('publicBaseUrl', v)}
              placeholder="https://garage.yourname.ts.net"
              supportingText={t('settings.customerPortal.publicBaseUrlHelp')}
              error={errors.publicBaseUrl}
              fullWidth
            />
          </div>
        </div>
      </div>

          </div>

          {/* Access & System section - only rendered when active since it has no form state */}
          {validActiveSection === 'accessAndSystem' && (
            <div className="flex flex-col gap-4">
              <PasskeysSection />
              <StaffAccountsSection />
              <ActivityFeedSection />
              <SoftwareUpdatePanel />

      {/* Backup */}
      <div className="bg-surface-container border border-outline-variant rounded-lg p-5">
        <div>
          <h2 className="text-title-md text-on-surface">{t('settings.backup.title')}</h2>
          <p className="text-body-sm text-on-surface-variant mt-0.5">{t('settings.backup.subtitle')}</p>

          <div className="space-y-4 mt-4">
            <p className="text-body-sm text-on-surface">
              {t('settings.backup.description')}
            </p>

            <a
              href={api.backupUrl()}
              download
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-outline
                text-on-surface text-label-lg interactive
                hover:bg-surface-high active:bg-surface-high w-full"
            >
              <DownloadIcon />
              {t('settings.backup.downloadBackup')}
            </a>
          </div>
        </div>
      </div>
          </div>
          )}
        </div>
      </div>
    </div>
  )
}
